import type { AnyField, Field } from "./fields";

/** An entity's id: whatever the id field carries (a ULID string, or a number). */
export type Id = string | number;

/**
 * A field for every property of T, the optional ones included: leave one
 * out and it won't compile, so a new property on a state type can't be
 * forgotten on the wire. Fields go on the wire (and in the change mask) in
 * the order they're written here: put the ones that change most first, so
 * the mask is usually one byte.
 */
export type Fields<T> = { [K in keyof T]-?: AnyField<T[K]> };

export interface Entity<T extends object = any> {
  readonly idKey: keyof T & string;
  readonly idField: Field<Id, any>;
  /** Every field but the id, in wire order. */
  readonly names: readonly (keyof T & string)[];
  readonly fields: readonly Field<unknown, unknown>[];
}

/** An entity type: which property is its id (sent once, when a peer first sees it), and a field for every other. */
export function entity<T extends object>(idKey: keyof T & string, fields: Fields<T>): Entity<T> {
  const idField = fields[idKey] as Field<Id, any> | undefined;
  if (!idField) throw new Error(`netsync: no field for the id property "${idKey}"`);
  const names = (Object.keys(fields) as (keyof T & string)[]).filter((n) => n !== idKey);
  return { idKey, idField, names, fields: names.map((n) => fields[n] as Field<unknown, unknown>) };
}

export type Types = Record<string, Entity>;

/** The entity types a snapshot carries, in wire order. */
export interface Protocol<M extends Types> {
  readonly types: M;
  readonly names: readonly (keyof M & string)[];
}

export function protocol<M extends Types>(types: M): Protocol<M> {
  return { types, names: Object.keys(types) as (keyof M & string)[] };
}

export type EntityOf<E> = E extends Entity<infer T> ? T : never;

/** The entity types of a protocol: `SnapshotClient<TypesOf<typeof MY_PROTOCOL>>`. */
export type TypesOf<P> = P extends Protocol<infer M> ? M : never;
